import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Menu, X } from 'lucide-react';
import { clearSession, getSessionStatus, isLoggedIn } from '../utils/session';
import './AppNav.css';

function roleHome(role) {
  if (role === 'Admin') return '/admin';
  return '/profile';
}

function buildLinks(role) {
  const common = [
    { to: '/profile', label: 'My profile' },
    { to: '/community', label: 'My community' },
    { to: '/volunteer', label: 'Volunteer' },
    { to: '/jobs', label: 'Jobs' },
    { to: '/talent', label: 'My talent' },
    { to: '/business', label: 'Local business' },
    { to: '/help', label: 'Need help' },
  ];

  const ops = [];
  if (role === 'Admin' || role === 'Area Head') {
    ops.push(
      { to: '/help/queue', label: 'Help queue' },
      { to: '/area-head/sos', label: 'SOS alerts' }
    );
  }
  if (role === 'Admin') {
    ops.push(
      { to: '/admin', label: 'Admin dashboard' },
      { to: '/admin/verify', label: 'Verification queue' },
      { to: '/admin/members', label: 'Manage members' }
    );
  }

  return { common, ops };
}

const ROOT_PATHS = new Set(['/profile', '/admin', '/login', '/apply']);

export default function AppNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const role = sessionStorage.getItem('sangramUserRole') || '';
  const name = sessionStorage.getItem('sangramUserName') || '';
  const status = getSessionStatus();
  const home = roleHome(role);
  const links = useMemo(() => buildLinks(role), [role]);
  const canGoBack = !ROOT_PATHS.has(pathname);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!isLoggedIn() || status !== 'Active') return null;

  const goBack = () => {
    if (canGoBack && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(home);
  };

  const logout = () => {
    clearSession();
    setOpen(false);
    navigate('/login', { replace: true });
  };

  return (
    <>
      <header className="app-nav-bar">
        <button
          type="button"
          className="app-nav-icon-btn"
          onClick={goBack}
          aria-label={canGoBack ? 'Go back' : 'Go home'}
        >
          <ArrowLeft size={22} strokeWidth={2.25} aria-hidden />
        </button>
        <p className="app-nav-brand">Sangram Sena</p>
        <button
          type="button"
          className="app-nav-icon-btn"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          aria-expanded={open}
        >
          <Menu size={22} strokeWidth={2.25} aria-hidden />
        </button>
      </header>

      {open ? (
        <button type="button" className="app-nav-scrim" aria-label="Close menu" onClick={() => setOpen(false)} />
      ) : null}

      <nav className={`app-nav-drawer${open ? ' open' : ''}`} aria-hidden={!open} aria-label="App menu">
        <div className="app-nav-drawer-head">
          <div>
            <h2 className="app-nav-drawer-title">{name || 'Menu'}</h2>
            <p className="app-nav-drawer-meta">
              {role || 'Member'}
              {status ? ` · ${status}` : ''}
            </p>
          </div>
          <button type="button" className="app-nav-icon-btn" onClick={() => setOpen(false)} aria-label="Close menu">
            <X size={22} strokeWidth={2.25} aria-hidden />
          </button>
        </div>

        <ul className="app-nav-list">
          {links.common.map((item) => (
            <li key={item.to}>
              <Link
                className={`app-nav-link${pathname === item.to ? ' active' : ''}`}
                to={item.to}
                onClick={() => setOpen(false)}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        {links.ops.length ? (
          <>
            <p className="app-nav-section">Operations</p>
            <ul className="app-nav-list">
              {links.ops.map((item) => (
                <li key={item.to}>
                  <Link
                    className={`app-nav-link${pathname === item.to ? ' active' : ''}`}
                    to={item.to}
                    onClick={() => setOpen(false)}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        <ul className="app-nav-list">
          <li>
            <button type="button" className="app-nav-link danger" onClick={logout}>
              Log out
            </button>
          </li>
        </ul>
      </nav>
    </>
  );
}
